import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { LAYER_LEGENDS } from "../config/legend";
import type { DeforestationLayerStates } from "../types/deforestation";
import { TruncatedText } from "components/shared/truncated-text";

interface LegendPanelProps {
  layers: DeforestationLayerStates;
}

export function LegendPanel({ layers }: LegendPanelProps) {
  // Dedupe by legend title — Forest 2020 and Forest 2025 share the
  // crown-cover palette, so showing both side-by-side is just noise.
  const seenTitles = new Set<string>();
  const active = (
    Object.keys(LAYER_LEGENDS) as Array<keyof DeforestationLayerStates>
  ).filter((key) => {
    if (!layers[key]?.visible) return false;
    const title = LAYER_LEGENDS[key]?.title;
    if (!title || seenTitles.has(title)) return false;
    seenTitles.add(title);
    return true;
  });

  if (active.length === 0) return null;

  return (
    <div className="border-t bg-card/95 p-3">
      <h3 className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        Legend
      </h3>
      <div className="space-y-2">
        {active.map((key) => {
          const legend = LAYER_LEGENDS[key];
          if (!legend) return null;
          // The QLD regulated-veg labels are formatted as "<code> — <descriptor>"
          // and overflow the legend column. Show a tooltip with just the
          // descriptor on hover. Other legends are short enough — no tooltip.
          const tooltipEnabled = key === "regulatedVeg";
          return (
            <div key={key}>
              <p className="mb-1 text-xs font-medium">{legend.title}</p>
              <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
                {legend.entries.map((entry) => {
                  // When the outer descriptor tooltip is enabled the label must
                  // stay a plain span — nesting TruncatedText's own tooltip
                  // inside it would produce two competing tooltips.
                  const row = (
                    <div className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground cursor-default">
                      <span
                        aria-hidden
                        className="h-3 w-3 shrink-0 rounded-full border border-border"
                        style={{ backgroundColor: entry.color }}
                      />
                      {tooltipEnabled ? (
                        <span className="truncate">{entry.label}</span>
                      ) : (
                        <TruncatedText as="span" className="min-w-0">
                          {entry.label}
                        </TruncatedText>
                      )}
                    </div>
                  );
                  if (!tooltipEnabled) return <div key={entry.label}>{row}</div>;
                  // Strip the "A — " / "Water — " prefix; if the label has no
                  // em-dash separator just use the whole thing.
                  const descriptor = entry.label.includes(" — ")
                    ? entry.label.split(" — ").slice(1).join(" — ")
                    : entry.label;
                  return (
                    <Tooltip key={entry.label}>
                      <TooltipTrigger asChild>{row}</TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs">
                        {descriptor}
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
